'use client';

import { c as _c } from "react/compiler-runtime";
import { useFrame } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import { attribute, float, positionLocal, sin, uniform, uv, vec3 } from 'three/tsl';
import { AdditiveBlending, BufferGeometry, CatmullRomCurve3, Color, DoubleSide, Float32BufferAttribute, type Mesh, MeshBasicNodeMaterial, Vector3 } from 'three/webgpu';
import type { MutableRef } from '@/app/lcv/components/sections/spartan/shared/webgpu/canvas-basic/types';
import { ENGINE_STAGE_RENDER_ORDER } from '../constants';
import type { AirflowRibbonConfig, EngineAirflowConfig } from './config/types';
const SEGMENTS = 96;
interface AirflowPlaybackProps {
  isAnimating?: boolean;
  enabled?: boolean;
  visibilityRef?: MutableRef<number>;
  // Compile invisibly with the initial scene rather than on Story entry.
  warmUp?: boolean;
}

// A narrow strip, not a full-screen quad: only the ribbon's pixels run the
// fragment shader. The curve is sampled once, never rebuilt during tuning.
const createRibbonGeometry = (side: number) => {
  const curve = new CatmullRomCurve3([new Vector3(-0.12 * side, -0.58, -0.08), new Vector3(0.38 * side, -0.38, -0.12), new Vector3(0.57 * side, -0.03, -0.02), new Vector3(0.47 * side, 0.35, 0.02), new Vector3(0.15 * side, 0.62, -0.1)]);
  const positions: number[] = [];
  const sides: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  for (let i = 0; i <= SEGMENTS; i++) {
    const t = i / SEGMENTS;
    const point = curve.getPointAt(t);
    const tangent = curve.getTangentAt(t);
    const across = new Vector3(-tangent.y, tangent.x, 0).normalize();
    for (let edge = 0; edge < 2; edge++) {
      positions.push(point.x, point.y, point.z);
      sides.push(across.x, across.y, across.z);
      uvs.push(t, edge);
    }
    if (i < SEGMENTS) {
      const a = i * 2;
      indices.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
    }
  }
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
  geometry.setAttribute('ribbonSide', new Float32BufferAttribute(sides, 3));
  geometry.setAttribute('uv', new Float32BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  return geometry;
};
const AirflowRibbon = t0 => {
  const $ = _c(37);
  const {
    config,
    side,
    isAnimating,
    enabled,
    visibilityRef,
    warmUp
  } = t0;
  const meshRef = useRef(null);
  let t1;
  if ($[0] !== side) {
    t1 = createRibbonGeometry(side);
    $[0] = side;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const geometry = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      time: uniform(0),
      tint: uniform(new Color()),
      opacity: uniform(0),
      brightness: uniform(1),
      speed: uniform(0),
      width: uniform(0.08),
      distortion: uniform(0)
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const nodes = t2;
  const writers = useRef(nodes);
  let t3;
  if ($[3] !== config.brightness || $[4] !== config.color || $[5] !== config.distortion || $[6] !== config.speed || $[7] !== config.width) {
    t3 = () => {
      const n = writers.current;
      n.tint.value.set(config.color);
      n.brightness.value = config.brightness;
      n.speed.value = config.speed;
      n.width.value = config.width;
      n.distortion.value = config.distortion;
    };
    $[3] = config.brightness;
    $[4] = config.color;
    $[5] = config.distortion;
    $[6] = config.speed;
    $[7] = config.width;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== config) {
    t4 = [config];
    $[9] = config;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  useEffect(t3, t4);
  let mat;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    mat = new MeshBasicNodeMaterial();
    const coords = uv();
    const taper = sin(coords.x.mul(Math.PI)).max(0);
    const edge = float(1).sub(coords.y.mul(2).sub(1).abs()).max(0).pow(2);
    const flow = sin(coords.x.sub(nodes.time.mul(nodes.speed)).mul(Math.PI * 2)).mul(0.5).add(0.5).pow(3).mul(0.75).add(0.25);
    mat.positionNode = positionLocal.add(attribute("ribbonSide", "vec3").mul(coords.y.sub(0.5)).mul(nodes.width)).add(vec3(sin(coords.x.mul(12).add(nodes.time.mul(0.7))).mul(nodes.distortion).mul(taper), 0, 0));
    mat.colorNode = nodes.tint.mul(nodes.brightness);
    mat.opacityNode = edge.mul(taper).mul(flow).mul(nodes.opacity);
    mat.transparent = true;
    mat.blending = AdditiveBlending;
    mat.side = DoubleSide;
    mat.depthTest = false;
    mat.depthWrite = false;
    mat.toneMapped = false;
    $[11] = mat;
  } else {
    mat = $[11];
  }
  const material = mat;
  let t5;
  if ($[12] !== config.opacity || $[13] !== enabled || $[14] !== isAnimating || $[15] !== visibilityRef?.value || $[16] !== warmUp) {
    t5 = (_state, delta) => {
      const opacity = enabled ? config.opacity * (visibilityRef?.value ?? 1) : 0;
      writers.current.opacity.value = opacity;
      if (meshRef.current) {
        meshRef.current.visible = !!warmUp || opacity > 0.001;
      }
      if (isAnimating && opacity > 0.001) {
        writers.current.time.value = writers.current.time.value + Math.min(delta, 0.05);
      }
    };
    $[12] = config.opacity;
    $[13] = enabled;
    $[14] = isAnimating;
    $[15] = visibilityRef?.value;
    $[16] = warmUp;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  useFrame(t5);
  let t6;
  let t7;
  if ($[18] !== geometry) {
    t6 = () => () => geometry.dispose();
    t7 = [geometry];
    $[18] = geometry;
    $[19] = t6;
    $[20] = t7;
  } else {
    t6 = $[19];
    t7 = $[20];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => () => material.dispose();
    t9 = [material];
    $[21] = t8;
    $[22] = t9;
  } else {
    t8 = $[21];
    t9 = $[22];
  }
  useEffect(t8, t9);
  let t10;
  if ($[23] !== config.x || $[24] !== config.y || $[25] !== config.z) {
    t10 = [config.x, config.y, config.z];
    $[23] = config.x;
    $[24] = config.y;
    $[25] = config.z;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const t11 = config.rotation * Math.PI / 180;
  let t12;
  if ($[27] !== t11) {
    t12 = [0, 0, t11];
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== config.scaleX || $[30] !== config.scaleY) {
    t13 = [config.scaleX, config.scaleY, 1];
    $[29] = config.scaleX;
    $[30] = config.scaleY;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== geometry || $[33] !== t10 || $[34] !== t12 || $[35] !== t13) {
    t14 = <mesh ref={meshRef} geometry={geometry} material={material} frustumCulled={false} renderOrder={ENGINE_STAGE_RENDER_ORDER.linesBack} position={t10} rotation={t12} scale={t13} />;
    $[32] = geometry;
    $[33] = t10;
    $[34] = t12;
    $[35] = t13;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  return t14;
};

// Behind the canvas engine plate, and also behind the DOM engine. No
// pointer listeners or hit targets: hotspot interaction stays with the UI.
export const EngineAirflow = t0 => {
  const $ = _c(15);
  const {
    config,
    isAnimating: t1,
    enabled: t2,
    visibilityRef,
    warmUp: t3
  } = t0;
  const isAnimating = t1 === undefined ? true : t1;
  const enabled = t2 === undefined ? true : t2;
  const warmUp = t3 === undefined ? false : t3;
  let t4;
  if ($[0] !== config.blue || $[1] !== enabled || $[2] !== isAnimating || $[3] !== visibilityRef || $[4] !== warmUp) {
    t4 = <AirflowRibbon side={-1} config={config.blue} isAnimating={isAnimating} enabled={enabled} visibilityRef={visibilityRef} warmUp={warmUp} />;
    $[0] = config.blue;
    $[1] = enabled;
    $[2] = isAnimating;
    $[3] = visibilityRef;
    $[4] = warmUp;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== config.red || $[7] !== enabled || $[8] !== isAnimating || $[9] !== visibilityRef || $[10] !== warmUp) {
    t5 = <AirflowRibbon side={1} config={config.red} isAnimating={isAnimating} enabled={enabled} visibilityRef={visibilityRef} warmUp={warmUp} />;
    $[6] = config.red;
    $[7] = enabled;
    $[8] = isAnimating;
    $[9] = visibilityRef;
    $[10] = warmUp;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = <group>{t4}{t5}</group>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
};